import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewEncapsulation,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { remapEchartsColors, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { LineChart } from 'echarts/charts';
import { GridComponent, MarkPointComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { DataInputConfig } from '../model';
import { DataInputComponent } from '../data-input';
import { DEFAULT_PARAMS, EXPERIMENT_MAP, ProbabilityParams, TrialResult } from './probability.model';
import { buildChartOptions, buildDataInputConfig, runAllExperiments } from './probability.util';

echarts.use([LineChart, SVGRenderer, GridComponent, MarkPointComponent, TooltipComponent]);

@Component({
  selector: 'dojo-probability',
  standalone: true,
  imports: [NgxEchartsDirective, DataInputComponent],
  templateUrl: './probability.component.html',
  styleUrl: './probability.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
})
export class ProbabilityComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });

    effect(() => {
      const p = this.params();
      this.trialSizes.set([...p.trialSizes]);
      this.currentSeed.set(p.seed ?? 'default');
    });

    // Auto-simulate when trial sizes or seed change
    effect(() => {
      const sizes = this.trialSizes();
      const seed = this.currentSeed();
      const p = this.params();
      if (sizes.length > 0) {
        this.results.set(
          runAllExperiments({
            ...p,
            trialSizes: sizes,
            seed,
          }),
        );
      } else {
        this.results.set([]);
      }
    });
  }

  private readonly isDark;

  readonly initOpts = { renderer: 'svg' } as const;

  readonly params = input<ProbabilityParams>(DEFAULT_PARAMS);
  readonly height = input<number>(500);

  readonly currentSeed = signal('default');
  readonly trialSizes = signal<number[]>([...DEFAULT_PARAMS.trialSizes]);
  readonly results = signal<TrialResult[]>([]);

  readonly editingChipIndex = signal<number | null>(null);

  readonly experimentDef = computed(() => {
    const p = this.params();
    const base = EXPERIMENT_MAP[p.experiment];
    return p.targetOutcome && p.targetOutcome !== base.targetOutcome
      ? { ...base, targetOutcome: p.targetOutcome }
      : base;
  });

  readonly dataInputConfig = computed<DataInputConfig>(() => {
    return buildDataInputConfig(this.experimentDef(), this.results());
  });

  readonly chartOptions = computed<EChartsOption>(() => {
    const p = this.params();
    return buildChartOptions(this.experimentDef(), this.results(), p.chartYAxisRange);
  });

  readonly adaptedOptions = computed(() => remapEchartsColors(this.chartOptions(), this.document, this.isDark()));

  readonly showTable = computed(() => this.params().showTable !== false);
  readonly showChart = computed(() => this.params().showChart !== false);
  readonly editableTrialSizes = computed(() => this.params().editableTrialSizes !== false);
  readonly chartHeight = computed(() => this.params().chartHeight ?? 250);

  onSimulateClick(): void {
    // Generate a fresh random seed based on timestamp
    this.currentSeed.set(`run-${Date.now()}`);
  }

  addTrialSize(): void {
    const sizes = this.trialSizes();
    const maxSize = sizes.length > 0 ? Math.max(...sizes) : 0;
    const newSize = maxSize > 0 ? maxSize * 2 : 10;
    this.trialSizes.set([...sizes, newSize]);
  }

  removeTrialSize(index: number): void {
    const sizes = [...this.trialSizes()];
    sizes.splice(index, 1);
    this.trialSizes.set(sizes);
  }

  startEditChip(index: number): void {
    this.editingChipIndex.set(index);
  }

  onChipBlur(index: number, event: Event): void {
    const el = event.target as HTMLInputElement;
    const value = parseInt(el.value, 10);
    if (!isNaN(value) && value > 0) {
      const sizes = [...this.trialSizes()];
      sizes[index] = value;
      this.trialSizes.set(sizes);
    }
    this.editingChipIndex.set(null);
  }

  onChipKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
    } else if (event.key === 'Escape') {
      this.editingChipIndex.set(null);
    }
  }
}
